import React from 'react';
import { CountryCode } from '@dayetopia/types';
import BenefitsTable from '@components/daye-for-work/BenefitsTable';
import SlideAnimation from '@components/daye-for-work/SlideAnimation';
import TextImageCarouselAnimated from '@components/daye-for-work/TextImageCarouselAnimated';
import ThreeStatistics from '@components/daye-for-work/ThreeStatistics';
import TwoColProducts from '@components/daye-for-work/TwoColProducts';
import FeaturedIn from '@components/FeaturedIn';
import ImageGridCarousel from '@components/ImageGridCarousel';
import ProductsShowcase from '@components/ProductsShowcase';
import ProvioticsTestimonials from '@components/proviotics/ProvioticsTestimonials';
import ProvioticsZeroWaste from '@components/proviotics/ProvioticsZeroWaste';
import TextFeatureRow from '@components/proviotics/TextFeatureRow';
import SamplerSubscriptionModule from '@components/sampler/SamplerSubscriptionModule';
import ButtonRow from '@components/shared/ButtonRow';
import CardSlice from '@components/shared/CardSlice';
import CaseStudySection from '@components/shared/CaseStudySection';
import CertificatesSlice from '@components/shared/CertificatesSlice';
import ComparisonTableAndImage from '@components/shared/ComparisonTableAndImage';
import ConsultationsExplained from '@components/shared/ConsultationsExplained';
import ConsultationsMeetNurses from '@components/shared/ConsultationsMeetNurses';
import ContentCarousel from '@components/shared/ContentCarousel';
import CustomSlice from '@components/shared/CustomSlice';
import BannerGrid from '@components/shared/design/BannerGrid';
import CarouselModule from '@components/shared/design/CarouselModule';
import DonationHero from '@components/shared/design/DonationHero';
import FlexibleGrid from '@components/shared/design/FlexibleGrid';
import Grid from '@components/shared/design/Grid';
import HeadingMessage from '@components/shared/design/HeadingMessage';
import HeroBanner from '@components/shared/design/HeroBanner';
import HeroWithFullWidthImage from '@components/shared/design/HeroFullWidthImage';
import ImageAndContent from '@components/shared/design/ImageAndContent';
import InnerButtonSlider from '@components/shared/design/InnerButtonSlider';
import LinkTabs from '@components/shared/design/LinkTabs';
import PathogenList from '@components/shared/design/PathogenList';
import ProductCompareCards from '@components/shared/design/ProductCompareCards';
import TabsFeature from '@components/shared/design/TabsFeature';
import TestimonialsModule from '@components/shared/design/TestimonialsModule';
import Timeline from '@components/shared/design/Timeline';
import Endorsements from '@components/shared/Endorsements';
import FAQs from '@components/shared/FAQ';
import FireworkWidget from '@components/shared/FireworkWidget';
import GiftCardForm from '@components/shared/GiftCardForm';
import HeaderImage from '@components/shared/HeaderImage';
import HeaderWaitlist from '@components/shared/HeaderWaitlist';
import HeroSlider from '@components/shared/HeroSlider';
import HowItWorksSteps from '@components/shared/HowItWorksSteps';
import HPVFoundersResults from '@components/shared/HPVFoundersResults';
import IconDescriptionBoxes from '@components/shared/IconDescriptionBoxes';
import IconTextGrid from '@components/shared/IconTextGrid';
import ImageAndInfo from '@components/shared/ImageAndInfo';
import ImageSlice from '@components/shared/ImageSlice';
import ImageSliderSlice from '@components/shared/ImageSliderSlice';
import ImageText from '@components/shared/ImageText';
import ImageTextBulletList from '@components/shared/ImageTextBulletList';
import LinkCarousel from '@components/shared/LinkCarousel';
import ModalTriggersRow from '@components/shared/ModalTrigger';
import NewsletterSubscribe from '@components/shared/NewsletterSubscribe';
import OverlappingItems from '@components/shared/OverlappingItems';
import PositivesAndImage from '@components/shared/PositivesAndImage';
import PPCDidYouKnow from '@components/shared/PPCDidYouKnow';
import PPCPackages from '@components/shared/PPCPackages';
import PriorityTips from '@components/shared/PriorityTips';
import ProductComparisonTable from '@components/shared/ProductComparisonTable';
import ProductUpsellRow from '@components/shared/ProductUpsellRow';
import ScreeningParametersInfoSlider from '@components/shared/ScreeningParametersInfoSlider';
import SizedBox from '@components/shared/SizedBox';
import StepSlider from '@components/shared/StepSlider';
import STIFoundersResults from '@components/shared/STIFoundersResults';
import TestimonialsSlider from '@components/shared/TestimonialsSlider';
import TextSlice from '@components/shared/TextSlice';
import Tour from '@components/shared/Tour';
import Trustpilot from '@components/shared/Trustpilot';
import TwoColumnGrid from '@components/shared/TwoColumnGrid';
import TwoColumnImageAndText from '@components/shared/TwoColumnImageAndText';
import VideoGrid from '@components/shared/VideoGrid';
import VideoHalfRow from '@components/shared/VideoHalfRow';
import VideoSlice from '@components/shared/VideoSlice';
import VitalsSection from '@components/shared/VitalsSection';
import MicrobiomeVideo from '@components/shared/vms/MicrobiomeVideo';
import VmsAtHome from '@components/shared/vms/VmsAtHome';
import VMSFoundersResults from '@components/shared/VMSFoundersResults';
import VMSSpecialistCare from '@components/shared/VMSSpecialistCare';
import VMSSpecialistCarePrices from '@components/shared/VMSSpecialistCarePrices';
import VMSVaginalFlora from '@components/shared/VMSVaginalFlora';
import TamponsScience from '@components/TamponsScience';
import VitalsCarousel from '@components/VitalsCarousel';
import { PageSlice, PageSliceType, PageSliceTypeKey } from '@contracts';
import { getProvioticsData } from '@data/proviotics';
import { ProvioticsTableData, VMSTableData } from '@data/tablesData';
import ImageTextQuadrant from '@components/shared/ImageTextQuadrant';
import StripBanner from '@components/shared/design/StripBanner';
import QuoteText from '@components/shared/design/QuoteText';

interface Props {
  slice: PageSlice;
  country?: CountryCode;
}

const PageComponent: React.FC<Props> = ({ slice, country = CountryCode.GB }) => {
  const { zeroWaste } = getProvioticsData();
  const whyPutCBDCheck = slice.primary?.text?.html?.includes('Why put CBD on tampons?');

  switch (slice.slice_type as PageSliceTypeKey) {
    case PageSliceType.Trustpilot:
      return <Trustpilot slice={slice} />;
    case PageSliceType.ComparisonTable:
      let data;
      switch (slice.primary?.type) {
        case 'vms':
          data = VMSTableData;
          break;
        case 'proviotics':
        default:
          data = ProvioticsTableData;
          break;
      }
      return <ComparisonTableAndImage slice={slice} data={data} />;
    case PageSliceType.FeaturedProducts:
      return <ProductsShowcase slice={slice} />;
    case PageSliceType.VitalsCarousel:
      return <VitalsCarousel slice={slice} noGradient={true} />;
    case PageSliceType.FeaturedIn:
      return <FeaturedIn slice={slice} />;
    case PageSliceType.Spacer:
      return <SizedBox slice={slice} />;
    case PageSliceType.Text:
      return <TextSlice slice={slice} />;
    case PageSliceType.Image:
      return <ImageSlice slice={slice} />;
    case PageSliceType.ImageText:
      if (country === CountryCode.US && whyPutCBDCheck) return null;
      return <ImageText slice={slice} />;
    case PageSliceType.ImageGrid:
      return <ImageGridCarousel slice={slice} />;
    case PageSliceType.IconTextGrid:
      return <IconTextGrid slice={slice} country={country} />;
    case PageSliceType.TestimonialsSlider:
      return <TestimonialsSlider slice={slice} />;
    case PageSliceType.ScienceModule:
      return <TamponsScience />;
    case PageSliceType.FAQs:
      return <FAQs slice={slice} title={slice.primary?.title?.text} moreLink="https://yourdaye.zendesk.com/hc/en-us" country={country} />;
    case PageSliceType.TamponsModalRow:
      if (country === CountryCode.US) return null;
      return <ModalTriggersRow backgroundColor={'rgba(250, 234, 214, 1);'} />;
    case PageSliceType.SubscriptionModule:
      if (country === CountryCode.US) return null;
      return <SamplerSubscriptionModule slice={slice} />;
    case PageSliceType.Video:
      return <VideoSlice slice={slice} />;
    case PageSliceType.Tour:
      return <Tour slice={slice} />;
    case PageSliceType.Grid:
      return <Grid slice={slice} />;
    case PageSliceType.FlexibleGrid:
      return <FlexibleGrid slice={slice} />;
    case PageSliceType.Timeline:
      return <Timeline slice={slice} />;
    case PageSliceType.InnerButtonSlider:
      return <InnerButtonSlider slice={slice} />;
    case PageSliceType.ProductCompareCards:
      return <ProductCompareCards slice={slice} />;
    case PageSliceType.PathogenList:
      return <PathogenList slice={slice} />;
    case PageSliceType.HeroBanner:
      return <HeroBanner slice={slice} />;
    case PageSliceType.TextFeatureRow:
      return <TextFeatureRow slice={slice} />;
    case PageSliceType.Endorsements:
      if (slice.primary?.background_color) {
        return <ProvioticsTestimonials slice={slice} />;
      }
      return <Endorsements slice={slice} />;
    case PageSliceType.ZeroWaste:
      return <ProvioticsZeroWaste slice={slice} data={zeroWaste} />;
    case PageSliceType.GiftCardForm:
      return <GiftCardForm />;
    case PageSliceType.ButtonRow:
      return <ButtonRow slice={slice} />;
    case PageSliceType.IndentifierRow:
      return <div style={{ height: '0' }} id={slice.primary?.id.text} />;
    case PageSliceType.CaseStudySection:
      return <CaseStudySection slice={slice} />;
    case PageSliceType.ImageSlider:
      return <ImageSliderSlice slice={slice} />;
    case PageSliceType.TwoColumnGrid:
      return <TwoColumnGrid slice={slice} />;
    case PageSliceType.TwoColumnImageAndText:
      return <TwoColumnImageAndText slice={slice} />;
    case PageSliceType.IconDescriptionBoxes:
      return <IconDescriptionBoxes slice={slice} />;
    case PageSliceType.HowItWorksSteps:
      return <HowItWorksSteps slice={slice} />;
    case PageSliceType.ScreeningParametersInfoSlider:
      return <ScreeningParametersInfoSlider slice={slice} />;
    case PageSliceType.PositivesAndImage:
      return <PositivesAndImage slice={slice} />;
    case PageSliceType.ImageAndInfo:
      return <ImageAndInfo slice={slice} />;
    case PageSliceType.Certificates:
      return <CertificatesSlice slice={slice} />;
    case PageSliceType.Card:
      return <CardSlice slice={slice} />;
    case PageSliceType.VMSSpecialistCare:
      return <VMSSpecialistCare slice={slice} />;
    case PageSliceType.VMSSpecialistCarePrices:
      return <VMSSpecialistCarePrices slice={slice} />;
    case PageSliceType.VMSFoundersResults:
      return <VMSFoundersResults />;
    case PageSliceType.STIFoundersResults:
      return <STIFoundersResults />;
    case PageSliceType.HPVFoundersResults:
      return <HPVFoundersResults />;
    case PageSliceType.VMSVaginalFlora:
      return <VMSVaginalFlora slice={slice} />;
    case PageSliceType.VitalsSection:
      return <VitalsSection slice={slice} />;
    case PageSliceType.Newsletter:
      return <NewsletterSubscribe slice={slice} country={country} />;
    case PageSliceType.ImageTextBulletList:
      return <ImageTextBulletList slice={slice} />;
    case PageSliceType.VideoGrid:
      return <VideoGrid slice={slice} />;
    case PageSliceType.HeroSlider:
      return <HeroSlider slice={slice} />;
    case PageSliceType.HeaderImage:
      return <HeaderImage slice={slice} />;
    case PageSliceType.HeaderWaitlist:
      return <HeaderWaitlist slice={slice} country={country} />;
    case PageSliceType.ContentCarousel:
      return <ContentCarousel slice={slice} />;
    case PageSliceType.PriorityTips:
      return <PriorityTips slice={slice} />;
    case PageSliceType.OverlappingItems:
      return <OverlappingItems slice={slice} />;
    case PageSliceType.VmsAtHome:
      return <VmsAtHome slice={slice} />;
    case PageSliceType.LinkCarousel:
      return <LinkCarousel slice={slice} />;
    case PageSliceType.VMSMicrobiomeVideo:
      return <MicrobiomeVideo slice={slice} enabled={true} />;
    case PageSliceType.PPCPackages:
      return <PPCPackages slice={slice} />;
    case PageSliceType.PPCDidYouKnow:
      return <PPCDidYouKnow slice={slice} country={country} />;
    case PageSliceType.VideoHalfRow:
      return <VideoHalfRow slice={slice} />;
    case PageSliceType.ProductComparisonTable:
      return <ProductComparisonTable slice={slice} />;
    case PageSliceType.StepSlider:
      return <StepSlider slice={slice} />;
    case PageSliceType.ConsultationsExplained:
      return <ConsultationsExplained slice={slice} />;
    case PageSliceType.ConsultationsMeetNurses:
      return <ConsultationsMeetNurses slice={slice} />;
    case PageSliceType.ProductUpsellRow:
      return <ProductUpsellRow slice={slice} />;
    case PageSliceType.ThreeStatistics:
      return <ThreeStatistics slice={slice} />;
    case PageSliceType.SlideAnimation:
      return <SlideAnimation slice={slice} />;
    case PageSliceType.CustomSlice:
      return <CustomSlice slice={slice} />;
    case PageSliceType.HeadingMessage:
      return <HeadingMessage slice={slice} />;
    case PageSliceType.TextImageCarouselAnimated:
      return <TextImageCarouselAnimated slice={slice} />;
    case PageSliceType.BenefitsTable:
      return <BenefitsTable slice={slice} />;
    case PageSliceType.TwoColProducts:
      return <TwoColProducts slice={slice} />;
    case PageSliceType.FireworkWidget:
      return (
        <FireworkWidget
          type={slice.primary?.type}
          id={slice.primary?.playlist_id}
          size="large"
          maxWidth={slice.primary?.max_width.text}
          background={slice.primary?.background_color}
          showInCountries={slice.primary?.show_in_countries?.text}
        />
      );
    case PageSliceType.ImageAndContent:
      return <ImageAndContent slice={slice} />;
    case PageSliceType.HeroFullWidthImage:
      return <HeroWithFullWidthImage slice={slice} />;
    case PageSliceType.TabsFeature:
      return <TabsFeature slice={slice} />;
    case PageSliceType.BannerGrid:
      return <BannerGrid slice={slice} />;
    case PageSliceType.LinkTabs:
      return <LinkTabs slice={slice} />;
    case PageSliceType.CarouselModule:
      return <CarouselModule slice={slice} />;
    case PageSliceType.DonationHero:
      return <DonationHero slice={slice} />;
    case PageSliceType.TestimonialsModule:
      return <TestimonialsModule slice={slice} />;
    case PageSliceType.ImageTextQuadrant:
      return <ImageTextQuadrant slice={slice} />;
    case PageSliceType.StripBanner:
      return <StripBanner slice={slice} />;
    case PageSliceType.QuoteText:
      return <QuoteText slice={slice} />;
    default:
      return null;
  }
};

export default PageComponent;
